import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  AccountTree as TreeIcon,
  Person as PersonIcon,
  SwapHoriz as FlowIcon,
  Flag as CheckpointIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import Markdown from 'react-markdown';
import { ArchitectureOutput } from '../../../services/workflow';
import { exportArchitectureToPdf } from '../../../utils/pdfExport';

interface ArchitectureSpecEditorProps {
  architecture: ArchitectureOutput | null;
  qualityScore?: number;
  qualityScoreLabel?: string;
  isLoading?: boolean;
}

// Convert architecture to markdown
const architectureToMarkdown = (arch: ArchitectureOutput): string => {
  // Safely handle agents array
  const agents = arch.agents || [];
  const agentList =
    agents.length > 0
      ? agents
          .map((a) => {
            const responsibilities = Array.isArray(a.responsibilities)
              ? a.responsibilities.map((r) => `- ${r}`).join('\n')
              : a.responsibilities || 'Not specified';
            const tools =
              Array.isArray(a.tools) && a.tools.length > 0
                ? a.tools.join(', ')
                : 'None specified';
            return `### ${a.name}\n**Role:** ${a.role}\n\n**Responsibilities:**\n${responsibilities}\n\n**Tools:** ${tools}`;
          })
          .join('\n\n---\n\n')
      : 'No agents defined yet.';

  // Safely handle communication_flows array
  const flows = arch.communication_flows || [];
  const flowList =
    flows.length > 0
      ? flows
          .map((f) => `- **${f.from}** → **${f.to}**: ${f.trigger}`)
          .join('\n')
      : '- No communication flows defined yet.';

  // Safely handle human_checkpoints array
  const checkpoints = arch.human_checkpoints || [];
  const checkpointList =
    checkpoints.length > 0
      ? checkpoints.map((c) => `- ${c}`).join('\n')
      : '- No human checkpoints defined.';

  return `# Multi-Agent Architecture Specification

## Architecture Overview

**Type:** ${arch.is_multi_agent ? 'Multi-Agent System' : 'Single Agent'}
**Orchestration Pattern:** ${arch.orchestration_pattern.charAt(0).toUpperCase() + arch.orchestration_pattern.slice(1)}
**Root Agent:** ${arch.root_agent || 'Not specified'}

---

## Agent Roster

${agentList}

---

## Communication Flows

${flowList}

---

## Human Checkpoints

${checkpointList}

---
*Generated by Systems Architect*
`;
};

const ArchitectureSpecEditor: React.FC<ArchitectureSpecEditorProps> = ({
  architecture,
  qualityScore,
  qualityScoreLabel = 'Completeness',
  isLoading = false,
}) => {
  const [tab, setTab] = useState(0);
  const lastArchitecture = useRef<ArchitectureOutput | null>(architecture);

  useEffect(() => {
    if (architecture !== lastArchitecture.current) {
      lastArchitecture.current = architecture;
    }
  }, [architecture]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  // Empty state
  if (!architecture) {
    return (
      <Paper
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          bgcolor: 'background.default',
        }}
      >
        <TreeIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="body2" color="text.secondary" textAlign="center">
          The Systems Architect will analyze your design and recommend an
          architecture.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          The specification will appear here shortly.
        </Typography>
      </Paper>
    );
  }

  const markdown = architectureToMarkdown(architecture);

  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header with quality score */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          px: 2,
          pt: 1,
          pb: 0.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight="bold">
            Architecture Specification
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={
                architecture.is_multi_agent ? 'Multi-Agent' : 'Single Agent'
              }
              size="small"
              color={architecture.is_multi_agent ? 'primary' : 'default'}
              variant="outlined"
            />
            {qualityScore !== undefined && (
              <Chip
                label={`${qualityScoreLabel}: ${Math.round(qualityScore * 100)}%`}
                size="small"
                color={
                  qualityScore >= 0.8
                    ? 'success'
                    : qualityScore >= 0.5
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
              />
            )}
            <Tooltip title="Export as PDF">
              <IconButton
                size="small"
                onClick={() => exportArchitectureToPdf(architecture)}
              >
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        {qualityScore !== undefined && (
          <LinearProgress
            variant="determinate"
            value={qualityScore * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor:
                  qualityScore >= 0.8
                    ? 'success.main'
                    : qualityScore >= 0.5
                      ? 'warning.main'
                      : 'grey.400',
              },
            }}
          />
        )}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="architecture spec tabs"
        >
          <Tab label="Preview" />
          <Tab label="Details" />
        </Tabs>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress />}

      {/* Content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {tab === 0 ? (
          // Preview tab - styled markdown
          <Box
            className="markdown-preview"
            sx={{
              '& h1': {
                fontSize: '1.5rem',
                mb: 2,
                color: 'primary.main',
                fontWeight: 600,
              },
              '& h2': {
                fontSize: '1.2rem',
                mt: 3,
                mb: 1,
                color: 'text.primary',
                borderBottom: 1,
                borderColor: 'divider',
                pb: 0.5,
                fontWeight: 600,
              },
              '& h3': { fontSize: '1rem', mt: 2, mb: 0.5, fontWeight: 600 },
              '& p': { mb: 1, lineHeight: 1.6 },
              '& ul': { pl: 2, mb: 1 },
              '& li': { mb: 0.5 },
              '& hr': { my: 2, borderColor: 'divider' },
              '& strong': { fontWeight: 600 },
            }}
          >
            <Markdown>{markdown}</Markdown>
          </Box>
        ) : (
          // Details tab - structured view
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Architecture Type */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Architecture Type
              </Typography>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
              >
                <TreeIcon color="primary" />
                <Typography variant="body1" fontWeight="bold">
                  {architecture.is_multi_agent
                    ? 'Multi-Agent System'
                    : 'Single Agent'}
                </Typography>
                <Chip
                  label={architecture.orchestration_pattern}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>

            <Divider />

            {/* Agents */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Agent Roster ({(architecture.agents || []).length} agents)
              </Typography>
              <List dense>
                {(architecture.agents || []).map((agent, idx) => (
                  <ListItem key={idx} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <PersonIcon
                        color={
                          agent.name === architecture.root_agent
                            ? 'primary'
                            : 'action'
                        }
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="subtitle2">
                            {agent.name}
                          </Typography>
                          {agent.name === architecture.root_agent && (
                            <Chip label="Root" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={agent.role}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>

            <Divider />

            {/* Communication Flows */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Communication Flows (
                {(architecture.communication_flows || []).length})
              </Typography>
              <List dense>
                {(architecture.communication_flows || []).map((flow, idx) => (
                  <ListItem key={idx} sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <FlowIcon color="action" />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${flow.from} → ${flow.to}`}
                      secondary={flow.trigger}
                    />
                  </ListItem>
                ))}
                {(architecture.communication_flows || []).length === 0 && (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemText secondary="No communication flows defined yet." />
                  </ListItem>
                )}
              </List>
            </Box>

            <Divider />

            {/* Human Checkpoints */}
            <Box>
              <Typography variant="overline" color="text.secondary">
                Human Checkpoints (
                {(architecture.human_checkpoints || []).length})
              </Typography>
              <List dense>
                {(architecture.human_checkpoints || []).map(
                  (checkpoint, idx) => (
                    <ListItem key={idx} sx={{ pl: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckpointIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText primary={checkpoint} />
                    </ListItem>
                  )
                )}
                {(architecture.human_checkpoints || []).length === 0 && (
                  <ListItem sx={{ pl: 0 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Fully Autonomous"
                      secondary="No human approval required"
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ArchitectureSpecEditor;
